import { useNavigate } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import Header from "./general/header/Header";
import Signup from "./general/header/SignUp";
import DefaultButton from "./general/buttons/DefaultButton";
import { useAuth } from "../services/auth";
import { colors } from "../theme";

const features = [
    {
        icon: GroupsIcon,
        title: "One playlist, everyone's hands",
        body:
            "Invite friends as contributors and build a shared list together.",
    },
    {
        icon: SyncAltIcon,
        title: "Lands in your own account",
        body:
            "Shufl pushes the shared list into a real playlist on your own Spotify or Apple Music. Don't worry about a web player you have to keep open.",
    },
    {
        icon: FactCheckIcon,
        title: "Nothing syncs by surprise",
        body:
            "Every sync starts as a preview. See exactly what's about to be added or removed, untick anything you don't want, and only then hit confirm.",
    },
    {
        icon: LibraryMusicIcon,
        title: "Matched once, remembered",
        body:
            "Songs are matched across platforms by ISRC and cached, so a track someone added on Spotify is already linked when it reaches an Apple Music library. Hopefully leading to more accurate matching.",
    },
];

const steps = [
    { n: "01", title: "Create a playlist", body: "Start a Shufl playlist and invite the people you want adding to it." },
    { n: "02", title: "Connect your service", body: "Link your Spotify or Apple Music account. Then point the playlist at one of your own playlists." },
    { n: "03", title: "Review the preview", body: "Hit sync to see the additions and removals queued up for your account." },
    { n: "04", title: "Confirm and listen", body: "Approve the changes and they land in your library, ready to play any time." },
];

const platforms = [
    { name: "Spotify", icon: "/assets/spotify_icon.png", status: "Available now" },
    { name: "Apple Music", icon: "/assets/apple_music_icon.png", status: "Available now" },
    { name: "YouTube Music", icon: "/assets/youtube_icon.png", status: "Coming Soon" },
];

export default function Home() {
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();

    return (
        <Box sx={{ backgroundColor: colors.base, minHeight: "100vh" }}>
            <Box sx={{ position: "sticky", top: 0, zIndex: 1100 }}>
                <Header />
            </Box>

            {/* ---------------- Hero ---------------- */}
            <Box
                component="section"
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    px: { xs: 3, md: 8 },
                    pt: { xs: 8, md: 12 },
                    pb: { xs: 10, md: 14 },
                    // Spotify-style colour wash bleeding out of the top of the page.
                    background: `radial-gradient(1100px 520px at 12% -10%, rgba(29,155,240,0.42) 0%, rgba(29,155,240,0.10) 42%, ${colors.base} 78%)`,
                }}
            >
                <Grid container spacing={{ xs: 6, md: 4 }} alignItems="center">
                    <Grid size={{ xs: 12, md: 6.5 }}>
                        <Typography
                            sx={{
                                color: colors.accent,
                                fontWeight: 800,
                                fontSize: 13,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                mb: 2,
                            }}
                        >
                            Collaborative playlists, synced properly
                        </Typography>

                        <Typography variant="h1" sx={{ mb: 3 }}>
                            Build the playlist
                            <Box component="span" sx={{ color: colors.accent }}> together</Box>.
                            Keep it in your own library.
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{ color: colors.textSecondary, fontSize: 18, maxWidth: 560, mb: 5 }}
                        >
                            Shufl is a shared playlist that doesn't trap you in one app. Everyone adds
                            tracks in one place, then syncs the result into their own Spotify or Apple
                            Music account - reviewing every change before it happens.
                        </Typography>

                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                            {isLoggedIn ? (
                                <DefaultButton
                                    text="Open your dashboard"
                                    size="large"
                                    onClick={() => navigate("/dashboard")}
                                />
                            ) : (
                                <Signup label="Get started free" size="large" />
                            )}
                            <DefaultButton
                                text="See how it works"
                                variant="secondary"
                                size="large"
                                onClick={() => {
                                    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Hero artwork - a mock playlist card built from bundled placeholder art. */}
                    <Grid size={{ xs: 12, md: 5.5 }}>
                        <Box
                            sx={{
                                borderRadius: 4,
                                overflow: "hidden",
                                border: `1px solid ${colors.line}`,
                                backgroundColor: colors.elevated,
                                boxShadow: "0 32px 80px rgba(0,0,0,0.55)",
                            }}
                        >
                            <Box
                                sx={{
                                    height: 190,
                                    backgroundImage: `linear-gradient(180deg, rgba(11,15,20,0.15) 0%, ${colors.elevated} 100%), url(/assets/hip-hop.webp)`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    display: "flex",
                                    alignItems: "flex-end",
                                    p: 3,
                                }}
                            >
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: colors.textSecondary, fontWeight: 700 }}>
                                        SHARED PLAYLIST
                                    </Typography>
                                    <Typography variant="h3">Friday Rotation</Typography>
                                    <Typography variant="body2">4 contributors · 38 songs</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ p: 2 }}>
                                {[
                                    { t: "HUMBLE.", a: "Kendrick Lamar", who: "added by Vanessa", img: "/assets/hero-song-humble.jpg" },
                                    { t: "NUEVAYoL", a: "Bad Bunny", who: "added by Chris", img: "/assets/hero-song-nuevayol.jpg" },
                                    { t: "Snooze", a: "SZA", who: "added by Juan", img: "/assets/hero-song-snooze.jpg" },
                                ].map((row, i) => (
                                    <Box
                                        key={row.t}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            px: 1.5,
                                            py: 1.25,
                                            borderRadius: 1,
                                            "&:hover": { backgroundColor: colors.elevatedHover },
                                        }}
                                    >
                                        <Typography sx={{ color: colors.textMuted, width: 16, fontSize: 14 }}>
                                            {i + 1}
                                        </Typography>
                                        <Box
                                            component="img"
                                            src={row.img}
                                            alt=""
                                            sx={{ width: 40, height: 40, borderRadius: 1, objectFit: "cover" }}
                                        />
                                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                            <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
                                                {row.t}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                                                {row.a}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{ fontSize: 12, color: colors.textMuted, display: { xs: "none", sm: "block" } }}
                                        >
                                            {row.who}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* ---------------- What is Shufl ---------------- */}
            <Box component="section" sx={{ px: { xs: 3, md: 8 }, py: { xs: 8, md: 12 } }}>
                <Typography variant="h2" sx={{ mb: 2, maxWidth: 720 }}>
                    What is Shufl?
                </Typography>
                <Typography variant="body1" sx={{ color: colors.textSecondary, fontSize: 17, maxWidth: 760, mb: 7 }}>
                    Group playlists usually mean everyone has to be on the same streaming service. 
                    Shufl keeps the playlist here and you just sync it to whatever service they already pay for.
                </Typography>

                <Grid container spacing={3}>
                    {features.map((feature) => (
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={feature.title}>
                            <Box
                                sx={{
                                    height: "100%",
                                    p: 3,
                                    borderRadius: 3,
                                    backgroundColor: colors.elevated,
                                    border: `1px solid ${colors.line}`,
                                    transition: "background-color 160ms ease, transform 160ms ease",
                                    "&:hover": { backgroundColor: colors.elevatedHover, transform: "translateY(-4px)" },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 46,
                                        height: 46,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: colors.accentSoft,
                                        color: colors.accent,
                                        mb: 2.5,
                                    }}
                                >
                                    <feature.icon sx={{ fontSize: 24 }} />
                                </Box>
                                <Typography variant="h5" sx={{ mb: 1.25 }}>{feature.title}</Typography>
                                <Typography variant="body2">{feature.body}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ---------------- How it works ---------------- */}
            <Box
                component="section"
                id="how-it-works"
                sx={{
                    px: { xs: 3, md: 8 },
                    py: { xs: 8, md: 12 },
                    backgroundColor: "#080C11",
                    borderTop: `1px solid ${colors.line}`,
                    borderBottom: `1px solid ${colors.line}`,
                }}
            >
                <Typography variant="h2" sx={{ mb: 6 }}>How it works</Typography>

                <Grid container spacing={3}>
                    {steps.map((step) => (
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={step.n}>
                            <Box sx={{ height: "100%", borderTop: `2px solid ${colors.accent}`, pt: 2.5 }}>
                                <Typography
                                    sx={{ color: colors.accent, fontWeight: 900, fontSize: 14, letterSpacing: "0.1em", mb: 1 }}
                                >
                                    {step.n}
                                </Typography>
                                <Typography variant="h5" sx={{ mb: 1 }}>{step.title}</Typography>
                                <Typography variant="body2">{step.body}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ---------------- Platforms ---------------- */}
            <Box component="section" sx={{ px: { xs: 3, md: 8 }, py: { xs: 8, md: 12 } }}>
                <Typography variant="h2" sx={{ mb: 2 }}>Works with your library</Typography>
                <Typography variant="body1" sx={{ color: colors.textSecondary, maxWidth: 640, mb: 6 }}>
                    Connect an account once. Shufl keeps the shared playlist and your personal one in
                    step from there.
                </Typography>

                <Grid container spacing={3}>
                    {platforms.map((platform) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={platform.name}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2.5,
                                    p: 3,
                                    borderRadius: 3,
                                    backgroundColor: colors.elevated,
                                    border: `1px solid ${colors.line}`,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={platform.icon}
                                    alt={platform.name}
                                    sx={{ width: 48, height: 48, borderRadius: 2, objectFit: "contain", flexShrink: 0 }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6" noWrap>{platform.name}</Typography>
                                    <Typography
                                        noWrap
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: platform.status === "Available now" ? colors.accent : colors.textMuted,
                                        }}
                                    >
                                        {platform.status}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ---------------- Closing CTA ---------------- */}
            <Box
                component="section"
                sx={{
                    mx: { xs: 3, md: 8 },
                    mb: { xs: 8, md: 12 },
                    px: { xs: 4, md: 8 },
                    py: { xs: 7, md: 9 },
                    borderRadius: 4,
                    textAlign: "center",
                    background: `linear-gradient(135deg, ${colors.accentPressed} 0%, #123A57 55%, ${colors.elevated} 100%)`,
                }}
            >
                <Typography variant="h2" sx={{ mb: 2 }}>Start your first shared playlist</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.82)", fontSize: 17, maxWidth: 560, mx: "auto", mb: 4 }}>
                    Free to create. Bring whichever streaming service you already use.
                </Typography>
                {isLoggedIn ? (
                    <DefaultButton text="Go to dashboard" size="large" onClick={() => navigate("/dashboard")} />
                ) : (
                    <Signup label="Get started free" size="large" />
                )}
            </Box>

            {/* ---------------- Footer ---------------- */}
            <Box
                component="footer"
                sx={{
                    px: { xs: 3, md: 8 },
                    py: 5,
                    borderTop: `1px solid ${colors.line}`,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Box
                    component="img"
                    src="/assets/Shufl.png"
                    alt="Shufl"
                    sx={{ height: 34, width: "auto", opacity: 0.85 }}
                />
                <Typography sx={{ color: colors.textMuted, fontSize: 13 }}>
                    Shufl - collaborative playlists that sync to the services you already use.
                </Typography>
            </Box>
        </Box>
    );
}
